import React, { useState } from 'react'
import styled from 'styled-components'
import { format } from 'date-fns'
import { colors, _ } from 'utils'
import { useEventSlug } from 'hooks'
import {
    MyEventsFragment, MyOrganizationFragment
} from 'generated/graphql'
import Button from 'components/Button'
import { useDispatch } from 'react-redux'
import { addModal } from 'features/modals/modalSlice'
import CreateEvent from 'features/events/CreateEvent'
import { SearchInput } from 'components/lists'
import Icon from 'components/Icon'
import constants from 'constants/constants'
import NoData from 'components/NoData'

const S = {
    Wrapper: styled.div`
        margin-top: 20px;
    `,
    SearchInput: styled(SearchInput)`
        width: 380px;
        margin: 0 20px 0 0;
    `,
    Link: styled.a`
        &:hover{
            text-decoration: none !important;
        }
    `,
    Row: styled.div`
        display: grid;
        grid-template-columns: 11fr 1fr;
        margin-bottom: 5px;
        padding: 8px 16px;
        border: 1px solid rgba(176, 190, 197, 0.15);
        border-radius: 4px;
        cursor: pointer;
        &:hover div:first-child {
            color: ${colors.primary};
            opacity: 1;
        }
    `,
    InfoWrapper: styled.div`
    `,
    EventName: styled.div<{ isActive: boolean }>`
        font-size: 14px;
        color: ${colors.dark} ${({ isActive }) => (isActive ? '!important' : '')};
        opacity: ${({ isActive }) => (isActive ? 1 : 0.75)};
    `,
    Date: styled.div`
        font-size: 12px;
        font-weight: 400;
        color: ${colors.icon};
        opacity: 0.75;
    `,
    CheckMark: styled.div`
        display: flex;
        align-items: center;
        justify-content: flex-end;
        opacity: 0.75;

        & > span {
            line-height: 16px;
        }
    `,
    Toolbar: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 32px;
    `,
}

export const formatDate = (date: string) => format(new Date(date), 'dd/MM/YYY')

type MyEvents = MyEventsFragment[]

interface Props {
    events: MyEvents
    closeListModal: () => void
    fetchData: () => void
    organizations: MyOrganizationFragment[]
}

const SwitchEventModal = ({
    events, closeListModal, fetchData, organizations
}: Props) => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const [search, setSearch] = useState('')
    const filterEvents = events.filter((e) => `${e.name} ${e.slug}`.includes(search))
    const isLite = constants.isLite
    const isOrganizationAdmin = organizations.length > 0

    // According to AC in https://twoppy.atlassian.net/browse/ORG-1381:
    // In the lite version: only organization admins can create events.
    const showCreateButton = !isLite || isOrganizationAdmin

    const showCreateEventModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CreateEvent
                    close={close}
                    closeListModal={closeListModal}
                    fetchData={fetchData}
                    organizations={organizations}
                />
            ),
            header: ' ',
        }))
    }

    return (
        <S.Wrapper>
            <S.Toolbar>
                <S.SearchInput onChange={setSearch} placeholder={_('event.search')} />
                {showCreateButton && (
                    <Button icon="plusBox" onClick={showCreateEventModal}>
                        {_('event.create_new_event')}
                    </Button>
                )}
            </S.Toolbar>
            {filterEvents.length ? filterEvents.map((event) => (
                <S.Link href={`/${event.slug}`}>
                    <S.Row key={event.name}>
                        <S.InfoWrapper>
                            <S.EventName isActive={eventSlug === event.slug}>
                                {event.name}
                            </S.EventName>
                            <S.Date>
                                {formatDate(event.startDate)}
                                {' to '}
                                {formatDate(event.endDate)}
                            </S.Date>
                        </S.InfoWrapper>
                        <S.CheckMark>
                            {
                                (eventSlug === event.slug) && (
                                    <Icon icon="check" size={20} color={colors.dark} />
                                )
                            }
                        </S.CheckMark>
                    </S.Row>
                </S.Link>
            )) : (
                <NoData />
            )}
        </S.Wrapper>
    )
}

export default SwitchEventModal
